"use client";

import type { StatisticsContent } from "@/components/landing/registry/content";
import { pickBi } from "@/components/landing/registry/locale";
import {
	Eyebrow,
	MotionReveal,
	SectionHeader,
	useSectionMotion,
} from "@/components/landing/shared";

interface StatisticsInnerProps {
	content: StatisticsContent;
	locale: string;
}

/**
 * Header + an airy grid of big-number stat tiles. Rendered inside
 * `SectionShell` so `useSectionMotion()` can read the instance's Motion tab
 * config via context.
 */
function StatisticsInner(prop: StatisticsInnerProps) {
	const { content, locale } = prop;
	const motion = useSectionMotion();
	const eyebrow = pickBi(content.eyebrow, locale);
	const title = pickBi(content.title, locale);
	const subtitle = pickBi(content.subtitle, locale);

	return (
		<>
			<MotionReveal motion={motion}>
				<SectionHeader
					eyebrow={eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : undefined}
					title={title}
					subtitle={subtitle}
					wrapperClassName="mx-auto max-w-2xl px-6 text-center"
				/>
			</MotionReveal>

			<div className="mx-auto mt-14 grid max-w-5xl grid-cols-2 gap-x-8 gap-y-10 px-6 text-center sm:grid-cols-3">
				{content.stats.map((stat, i) => (
					<MotionReveal key={`${i}-${stat.label.en}`} motion={motion} delayOffset={120 + i * 80}>
						<div>
							<p className="jana-gradient-text text-4xl font-extrabold tracking-tight sm:text-5xl">
								{pickBi(stat.value, locale)}
							</p>
							<p className="mt-2 text-sm text-muted-foreground">{pickBi(stat.label, locale)}</p>
						</div>
					</MotionReveal>
				))}
			</div>
		</>
	);
}

export default StatisticsInner;
